import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function SensorAirFlowmeter() {
  // --- SEO Meta Tags untuk Halaman Sensor Air & Flow Meter ---
  useEffect(() => {
    document.title = "Sensor Air & Flow Meter - Solusi IoT Cerdas | Kelola Sumber Daya Air";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', "Optimalkan penggunaan air dan pantau laju aliran dengan sensor Solusi IoT Cerdas. Solusi cerdas untuk irigasi, deteksi kebocoran, dan manajemen air.");

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', "sensor air, flow meter, IoT, manajemen air, irigasi cerdas, deteksi kebocoran");

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', "https://www.Solusi IoT Cerdas.com/produk/sensor/air-flowmeter"); // Ganti dengan URL halaman sebenarnya

    // Cleanup function: Akan dijalankan saat komponen di-unmount atau sebelum efek berikutnya
    return () => {
      document.title = "Solusi IoT Cerdas Solusi Cerdas IoT"; // Kembalikan ke judul default aplikasi
    };
  }, []); // Dependensi kosong agar useEffect hanya dijalankan sekali (saat mount)

  const [waterLevel, setWaterLevel] = useState(75);
  const [flowRate, setFlowRate] = useState(15);
  const [totalVolume, setTotalVolume] = useState(1250);

  // Simulasi riwayat data harian untuk grafik (dalam liter)
  const [dailyVolumeHistory, setDailyVolumeHistory] = useState(() => {
    const history = [];
    const today = new Date();
    // Inisialisasi data untuk 7 hari terakhir
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = date.toLocaleDateString('id-ID', { weekday: 'short' });
      // Simulasikan volume harian acak, cenderung meningkat ke hari ini
      const value = parseFloat((100 + (6 - i) * 20 + Math.random() * 50).toFixed(2));
      history.push({ day: dayName, date: date.toISOString().slice(0, 10), value: value });
    }
    return history;
  });

  // Effect untuk simulasi data real-time sensor
  useEffect(() => {
    const interval = setInterval(() => {
      const newTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      setWaterLevel(prev => {
        const change = (Math.random() * 5) - 2.5; // Fluktuasi kecil
        return parseFloat(Math.max(0, Math.min(100, prev + change)).toFixed(1));
      });
      setFlowRate(prev => {
        const change = (Math.random() * 5) - 2.5; // Fluktuasi kecil
        return parseFloat(Math.max(0, prev + change).toFixed(1));
      });
      // totalVolume diperbarui setiap 5 detik berdasarkan flowRate
      setTotalVolume(prev => parseFloat((prev + (flowRate * 5 / 60)).toFixed(2))); // flowRate * (interval_in_seconds / 60)

      // Update daily volume for today
      setDailyVolumeHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const todayIndex = updatedHistory.length - 1;
        const currentDay = updatedHistory[todayIndex];

        // Add a small random increment to today's volume based on flowRate
        // This makes the "daily" consumption feel real-time without rapidly changing the total daily value
        const increment = (flowRate * 5 / 3600); // Convert LPM * 5s to Liters/day (very small increment)
        updatedHistory[todayIndex] = {
          ...currentDay,
          value: parseFloat((currentDay.value + increment).toFixed(2))
        };
        return updatedHistory;
      });

    }, 5000); // Update setiap 5 detik

    return () => clearInterval(interval);
  }, [flowRate]); // Dependensi flowRate agar totalVolume update mengikuti flowRate yang baru

  // Data untuk grafik penggunaan air harian
  const chartData = {
    labels: dailyVolumeHistory.map(data => data.day),
    datasets: [
      {
        label: 'Penggunaan Air Harian (Liter)',
        data: dailyVolumeHistory.map(data => data.value),
        fill: true,
        backgroundColor: 'rgba(59, 130, 246, 0.2)', // blue-500 with opacity
        borderColor: '#3b82f6', // blue-500
        tension: 0.3, // Membuat garis sedikit melengkung
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#3b82f6',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'var(--text-color)', // Akan disesuaikan oleh CSS
        },
      },
      title: {
        display: true,
        text: 'Riwayat Penggunaan Air 7 Hari Terakhir',
        color: 'var(--text-color)', // Akan disesuaikan oleh CSS
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(context.parsed.y) + ' Liter';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: 'var(--text-color)',
        },
        grid: {
          color: 'rgba(100, 100, 100, 0.1)',
        },
      },
      y: {
        ticks: {
          color: 'var(--text-color)',
          callback: function(value) {
            return value + ' L';
          }
        },
        grid: {
          color: 'rgba(100, 100, 100, 0.1)',
        },
      },
    },
  };

  return (
    // Kontainer utama: bg-gray-100 di light, dark:bg-gray-900 di dark. Teks utama gray-800 di light, gray-200 di dark.
    <div className="container mx-auto p-4 md:p-8 bg-gray-100 text-gray-800 font-sans
                    dark:bg-gray-900 dark:text-gray-200 transition-colors duration-500">

      {/* Hero Section */}
      {/* Latar belakang hero section tetap gradasi biru (light mode), dan lebih gelap di dark mode */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-xl p-6 md:p-10 mb-8 text-center
                          dark:from-blue-800 dark:to-blue-900 transition-colors duration-500">
        <h1 className="text-3xl md:text-5xl font-bold mb-4">
          Kelola Sumber Daya Air Anda: Sensor Level Air & Flow Meter Solusi IoT Cerdas
        </h1>
        <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8">
          Optimalkan penggunaan air, pantau ketinggian cairan di tangki, dan ukur laju aliran air dengan akurat.
        </p>
        <div className="flex flex-col md:flex-row justify-center gap-6 mt-8">
          {/* Kartu di Hero Section: background abu-abu terang di light, abu-abu gelap di dark. */}
          <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-500 hover:scale-105
                          dark:bg-gray-800 dark:text-blue-200">
            <h3 className="text-xl font-semibold mb-2">Level Air Saat Ini</h3>
            <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{waterLevel}%</h2>
            <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Dalam Tangki</p>
          </div>
          <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-500 hover:scale-105
                          dark:bg-gray-800 dark:text-blue-200">
            <h3 className="text-xl font-semibold mb-2">Laju Aliran Saat Ini</h3>
            <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{flowRate} LPM</h2>
            <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Liter Per Menit</p>
          </div>
          <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-500 hover:scale-105
                          dark:bg-gray-800 dark:text-blue-200">
            <h3 className="text-xl font-semibold mb-2">Total Konsumsi</h3>
            <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{totalVolume.toFixed(2)} Liter</h2>
            <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Sejak Pemantauan</p>
          </div>
        </div>
      </section>

      {/* Bagian Grafik Penggunaan Air */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">
          Tren Penggunaan Air Harian Anda
        </h2>
        <div className="h-80 w-full"> {/* Atur tinggi grafik */}
          <Line data={chartData} options={chartOptions} />
        </div>
        <p className="text-center text-gray-600 text-sm mt-4 dark:text-gray-400">
          Data ini disimulasikan dan diperbarui secara *real-time*.
        </p>
      </section>

      {/* Bagian lainnya: background putih/abu-abu terang di light, abu-abu gelap di dark */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">Air adalah Sumber Daya Berharga</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Manajemen air yang efisien adalah kunci untuk rumah tangga, pertanian, maupun industri.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:text-gray-200 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-400">Hemat Air & Biaya</h3>
            <p className="text-gray-600 dark:text-gray-400">Identifikasi penggunaan berlebihan dan potensi kebocoran untuk efisiensi biaya.</p>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:text-gray-200 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-400">Pencegahan Masalah</h3>
            <p className="text-gray-600 dark:text-gray-400">Pantau level tangki untuk menghindari luapan atau kekosongan.</p>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:text-gray-200 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-400">Optimasi Irigasi</h3>
            <p className="text-gray-600 dark:text-gray-400">Sesuaikan penyiraman tanaman berdasarkan kebutuhan aktual.</p>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:text-gray-200 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-400">Manajemen Proses</h3>
            <p className="text-gray-600 dark:text-gray-400">Pastikan aliran cairan sesuai standar produksi dalam aplikasi industri.</p>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">Pantau Ketinggian Cairan Anda: Sensor Level Air</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Sensor level air memungkinkan Anda mengetahui volume cairan di tangki, kolam, atau wadah lainnya secara *real-time*.
        </p>
        <ul className="list-none p-0 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Indikator Level Visual</strong>: Lihat ketinggian air dengan mudah di *dashboard* Solusi IoT Cerdas Anda.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Alerte Ambang Batas</strong>: Dapatkan notifikasi jika level air terlalu rendah atau terlalu tinggi.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Riwayat Level</strong>: Analisis pola konsumsi atau pengisian tangki.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Aplikasi Umum</strong>: Pemantauan tandon air rumah, level air sumur, sistem irigasi, pemantauan akuarium.
          </li>
        </ul>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">Ukur Laju Aliran dengan Akurat: Flow Meter</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          *Flow meter* memberikan data tentang seberapa cepat air mengalir melalui pipa, membantu Anda mengukur konsumsi, mendeteksi kebocoran, atau mengontrol proses pengisian.
        </p>
        <ul className="list-none p-0 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Laju Aliran Real-time</strong>: Tampilan langsung berapa liter per menit (LPM) atau liter per jam (LPH) yang mengalir.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Total Konsumsi</strong>: Hitung total volume air yang telah melewati *flow meter*.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Deteksi Anomali</strong>: Dapatkan peringatan untuk aliran yang tidak biasa.
          </li>
          <li className="bg-gray-100 p-4 rounded-md border-l-4 border-blue-500 shadow-sm
                          dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
            <strong className="text-blue-700 dark:text-blue-300">Aplikasi Umum</strong>: Pemantauan konsumsi air rumah tangga, sistem irigasi presisi, deteksi kebocoran pipa.
          </li>
        </ul>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">Solusi Terpadu untuk Manajemen Air</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Kombinasi sensor level air dan *flow meter* melalui Solusi IoT Cerdas membuka potensi otomasi dan pemantauan yang lebih canggih:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-yellow-700 mb-2 dark:text-yellow-400">Sistem Irigasi Otomatis</h3>
            <p className="text-gray-600 dark:text-gray-400">Sensor level air mendeteksi tangki kosong, *flow meter* memastikan volume penyiraman yang tepat.</p>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-yellow-700 mb-2 dark:text-yellow-400">Pengisian Tangki Otomatis</h3>
            <p className="text-gray-600 dark:text-gray-400">Isi tangki saat level rendah dan berhenti saat level penuh.</p>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md col-span-1 md:col-span-2
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-yellow-700 mb-2 dark:text-yellow-400">Deteksi Kebocoran Lanjut</h3>
            <p className="text-gray-600 dark:text-gray-400">Jika level tangki turun tanpa aliran air yang terdeteksi, kemungkinan ada kebocoran.</p>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 text-center mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 dark:text-gray-100">Siap Mengoptimalkan Penggunaan Air Anda?</h2>
        <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto dark:text-gray-300">
          Proses integrasi sensor level air dan *flow meter* ke Solusi IoT Cerdas sangat sederhana.
        </p>
        <button className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg text-xl
                           transition-all duration-300 ease-in-out transform hover:scale-105"
                onClick={() => alert('Link ke Panduan Integrasi atau halaman Pendaftaran!')}>
          Pelajari Panduan Integrasi & Daftar Sekarang
        </button>
      </section>
    </div>
  );
}

export default SensorAirFlowmeter;